import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';

const SERVER_KEY = 'app.firstwhistle.my';

export const biometricService = {
  /**
   * Check if biometric authentication is available on the device
   */
  async isAvailable() {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    // Check if plugin is actually registered
    try {
      // Verify the plugin exists in Capacitor's registry
      if (window.Capacitor?.Plugins?.NativeBiometric) {
        console.log('[Biometric] Plugin found in Capacitor registry');
      } else {
        console.warn('[Biometric] Plugin not found in Capacitor registry. Make sure you have:');
        console.warn('  1. Run "npx cap sync ios"');
        console.warn('  2. Clean build folder in Xcode (Cmd+Shift+K)');
        console.warn('  3. Rebuild the app in Xcode');
        return false;
      }
    } catch (e) {
      // Plugin registry check failed, but continue anyway
      console.warn('[Biometric] Could not verify plugin registration:', e);
    }

    try {
      const result = await NativeBiometric.isAvailable();
      if (result.isAvailable) {
        console.log('[Biometric] Biometric authentication is available:', {
          authenticationStrength: result.authenticationStrength,
          isSimulator: Capacitor.getPlatform() === 'ios' && process.env.NODE_ENV === 'development'
        });
      } else {
        console.log('[Biometric] Biometric authentication not available. Make sure Face ID is enabled in iOS Simulator: Features > Face ID > Enrolled');
        if (result.errorCode !== undefined) {
          console.log('[Biometric] Error code:', result.errorCode);
        }
      }
      return result.isAvailable;
    } catch (error) {
      // Handle UNIMPLEMENTED error - plugin might not be ready yet or not properly configured
      if (error.code === 'UNIMPLEMENTED' || error.message?.includes('UNIMPLEMENTED')) {
        console.warn('[Biometric] Plugin not ready yet (UNIMPLEMENTED). This means the native code is not registered.');
        console.warn('[Biometric] To fix this:');
        console.warn('  1. Stop the app in Xcode');
        console.warn('  2. In Xcode: Product > Clean Build Folder (Cmd+Shift+K)');
        console.warn('  3. In Xcode: Product > Build (Cmd+B)');
        console.warn('  4. Run the app again');
        console.warn('[Biometric] The plugin is installed but needs a clean rebuild to register.');
        return false;
      }
      console.error('[Biometric] Error checking biometric availability:', error);
      return false;
    }
  },

  /**
   * Check if credentials are stored in keychain
   */
  async hasStoredCredentials() {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      await NativeBiometric.getCredentials({ server: SERVER_KEY });
      return true;
    } catch (error) {
      // Credentials not found - this is normal if user hasn't set up biometric login yet
      // KeychainError error 1 typically means "item not found" which is expected
      if (error.message?.includes('KeychainError') || error.errorMessage?.includes('KeychainError')) {
        // This is expected - no credentials stored yet
        return false;
      }
      // Log other errors but still return false
      console.warn('[Biometric] Error checking for stored credentials:', error.message || error.errorMessage);
      return false;
    }
  },

  /**
   * Store user credentials securely in native keychain
   * @param {string} phone - User's phone number
   * @param {string} password - User's password (encrypted by keychain)
   */
  async storeCredentials(phone, password) {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('Biometric storage only available on native platforms');
    }

    try {
      await NativeBiometric.setCredentials({
        username: phone,
        password: password,
        server: SERVER_KEY,
      });
      console.log('Credentials stored successfully in keychain');
      return true;
    } catch (error) {
      console.error('Error storing credentials:', error);
      throw error;
    }
  },

  /**
   * Retrieve credentials from keychain using biometric authentication
   * @returns {Promise<{phone: string, password: string}>}
   */
  async getCredentials() {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('Biometric authentication only available on native platforms');
    }

    try {
      // First verify biometric identity
      await NativeBiometric.verifyIdentity({
        reason: 'Log in to your account',
        title: 'Biometric Login',
        subtitle: 'Use your fingerprint or face recognition',
        description: 'Place your finger on the sensor or look at the camera',
      });

      // If biometric verification succeeds, retrieve credentials
      const credentials = await NativeBiometric.getCredentials({
        server: SERVER_KEY,
      });

      return {
        phone: credentials.username,
        password: credentials.password,
      };
    } catch (error) {
      console.error('Biometric authentication failed:', error);
      throw error;
    }
  },

  /**
   * Delete stored credentials from keychain
   */
  async deleteCredentials() {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      await NativeBiometric.deleteCredentials({
        server: SERVER_KEY,
      });
      console.log('Credentials deleted from keychain');
      return true;
    } catch (error) {
      console.error('Error deleting credentials:', error);
      return false;
    }
  },
};

